import { useState, useCallback } from 'react';
import { format, isSameDay } from 'date-fns';

import ShiftAssigmentService from '../services/shift-assignment-service'; 
import { isSuccess } from '../../../models/response/base-response';
import { showToast } from '../../../components/custom/custom-toast';

import { 
    EmployeeShiftCalendar, 
    ShiftCalendarItem, 
    CreateAssignmentPayload,
    DeleteAssignmentPayload
} from '../types/shift-assignment.types'; 

export const useScheduleInteraction = (
    employeeSchedules: EmployeeShiftCalendar[],
    loadCalendar: (weekStart: Date) => Promise<void>,
    weekDays: Date[],
    branchId?: number,
) => {
    const [activePopover, setActivePopover] = useState<{ day: Date, employeeId: string } | null>(null);
    const [activeActionMenu, setActiveActionMenu] = useState<{ day: Date, employeeId: string } | null>(null);
    const [removeModalState, setRemoveModalState] = useState<{ mode: 'single' | 'multiple'; data: any } | null>(null); 
    const [createModalState, setCreateModalState] = useState<{ isOpen: boolean; initialValues: any; mode: 'create' | 'edit'; } | null>(null);
    const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const [quickConfirmAssignState, setQuickConfirmAssignState] = useState<{ 
        isOpen: boolean; 
        data: { shiftId: string, day: Date, employeeId: string }; 
    } | null>(null);

    const { fetch: fetchCreateAssignment } = ShiftAssigmentService.createShiftAssignments(); 
    const { fetch: fetchDeleteAssignment } = ShiftAssigmentService.deleteShiftAssignment();
    const { fetch: fetchRemoveAssignments } = ShiftAssigmentService.removeShiftAssignments();

    const handleOpenPopover = useCallback((day: Date, employeeId: string) => {
        const key = { day, employeeId };
        setActivePopover(prev => (
            prev && isSameDay(prev.day, day) && prev.employeeId === employeeId ? null : key
        ));
    }, []);

    const handleShiftSelect = useCallback((shiftId: string, day: Date, employeeId: string) => {
        setQuickConfirmAssignState({
            isOpen: true,
            data: { shiftId, day, employeeId },
        });
        setActivePopover(null);
    }, []); 

    const handleQuickConfirmAssign = useCallback(async () => {
        if (!quickConfirmAssignState) return;

        const { shiftId, day, employeeId } = quickConfirmAssignState.data;

        const payload: CreateAssignmentPayload = {
            employee_ids: [Number(employeeId)],
            start_date: format(day, 'dd/MM/yyyy'),
            end_date: format(day, 'dd/MM/yyyy'),
            note: 'Gán ca nhanh',
            ...(branchId ? { branch_id: branchId } : {}),
        };

        const res = await fetchCreateAssignment(shiftId, payload);

        if (res && isSuccess(res)) {
            showToast({ message: "Phân công ca thành công", type: "success" });
            await loadCalendar(weekDays[0]);
        } else {
            showToast({ message: res?.message || "Phân công ca thất bại", type: "error" });
        }

        setQuickConfirmAssignState(null);
    }, [fetchCreateAssignment, quickConfirmAssignState, loadCalendar, weekDays, branchId]);
    
    const handleOpenActionMenu = useCallback((day: Date, employeeId: string) => {
        const key = { day, employeeId };
        setActiveActionMenu(prev => (
            prev && isSameDay(prev.day, day) && prev.employeeId === employeeId ? null : key
        ));
    }, []);

    const handleRemoveShift = useCallback((removeMode: 'single' | 'multiple') => {
        if (!activeActionMenu) return;
        const { day, employeeId } = activeActionMenu;
        
        const employeeSchedule = employeeSchedules.find(e => e.employee.id === employeeId);
        const formattedDay = format(day, 'dd/MM/yyyy'); 
        const shiftItem = employeeSchedule?.shifts.find(s => s.date === formattedDay);

        const assignmentItemId = shiftItem?.id; 

        if (!assignmentItemId) {
            showToast({ message: "Không có ca làm việc để gỡ trong ngày này.", type: "info" });
            setActiveActionMenu(null);
            return;
        }

        setActiveActionMenu(null);

        setRemoveModalState({
            mode: removeMode,
            data: { 
                employeeId: employeeId, 
                day: day, 
                shiftCalendarItemId: assignmentItemId,
            },
        });
    }, [activeActionMenu, employeeSchedules]);

    const handleOpenCreateModal = useCallback((day: Date, employeeId: string, shiftData?: ShiftCalendarItem) => {
        setCreateModalState({
            isOpen: true,
            initialValues: shiftData 
                ? { ...shiftData, employeeId, date: day } 
                : { employeeId, date: day },
            mode: shiftData ? 'edit' : 'create',
        });
    }, []);
    
    const handleSingleShiftSubmit = useCallback(async (data: any) => {
        if (data.mode === 'create') {
            console.log("Xử lý tạo ca bằng Modal lớn:", data);
        } else if (data.mode === 'edit') {
            console.log("Xử lý sửa ca:", data);
        }
        setCreateModalState(null);
    }, []);

    const handleConfirmRemove = useCallback(async (confirmData: { reason: string, startDate?: string, endDate?: string }) => {
        if (!removeModalState) return;

        setIsDeleting(true);

        const { mode, data } = removeModalState;
        const { shiftCalendarItemId, employeeId } = data;
        const { reason, startDate, endDate } = confirmData;

        try {
            let res;
            if (mode === 'multiple') {
                if (!startDate || !endDate) {
                    showToast({ message: "Vui lòng chọn ngày bắt đầu và kết thúc.", type: "error" });
                    setIsDeleting(false);
                    return;
                }
                res = await fetchRemoveAssignments('', {
                    employee_id: Number(employeeId),
                    start_date: startDate,
                    end_date: endDate,
                    reason: reason,
                });
            } else {
                const payload: DeleteAssignmentPayload = { note: reason };
                res = await fetchDeleteAssignment(shiftCalendarItemId, payload);
            }

            if (res && isSuccess(res)) {
                showToast({ message: "Gỡ phân công thành công!", type: "success" });
                await loadCalendar(weekDays[0]);
            } else {
                showToast({ message: res?.message || "Gỡ phân công thất bại.", type: "error" });
            }
        } catch (error) {
            showToast({ message: "Đã có lỗi xảy ra.", type: "error" });
        } finally {
            setIsDeleting(false);
            setRemoveModalState(null);
        }
    }, [removeModalState, fetchDeleteAssignment, fetchRemoveAssignments, loadCalendar, weekDays]);

    const handleBatchSubmit = useCallback((data: any) => {
        console.log("Xử lý gán ca hàng loạt:", data);
        setIsBatchModalOpen(false);
    }, []);

    return {
        activePopover,
        activeActionMenu,
        removeModalState,
        createModalState,
        isBatchModalOpen,
        isDeleting,
        quickConfirmAssignState,
        
        setActivePopover,
        setActiveActionMenu,
        setRemoveModalState,
        setCreateModalState,
        setIsBatchModalOpen,
        setQuickConfirmAssignState,
        
        handleOpenPopover,
        handleShiftSelect,
        handleQuickConfirmAssign,
        handleOpenActionMenu,
        handleRemoveShift,
        handleOpenCreateModal,
        handleConfirmRemove,
        handleSingleShiftSubmit,
        handleBatchSubmit,
    };
};